import React, { Component, Fragment } from 'react';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import { AppContext } from '../../AppProvider';

import Button from '@material-ui/core/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import ErrorNotification from '../../components/common/ErrorNotification';
import ToggleButton from '../../components/Global/Buttons/Toggle';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import * as Queries from '../../graphQL/queries/carrierRelationship';
import * as Mutations from '../../graphQL/mutations/carrierRelationship';
import {
  create as createEPro,
  update as updateEPro,
  deleteItem as deleteEPro
} from '../../graphQL/mutations/ePro';
import { byCarrierId as getEProByCarrierIdQuery } from '../../graphQL/queries/ePro';
import { locationsByOrgIdQuery } from '../../graphQL/queries/locationQueries';

import FormItemContainer from '../../components/Global/Containers/FormItemContainer';
import Grid from '@material-ui/core/Grid';
import GridItem from '../../components/Global/GridItem';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';

import CarrierRelationshipDetailsStyles from './__styles__/CarrierRelationshipDetails.styles';

import CarrierRelationshipBasicInfo from '../../components/carrierRelationship/BasicInfo';
import CarrierRelationshipInsuranceInfo from '../../components/carrierRelationship/InsuranceInfo';
import CarrierRelationshipEProFormModal from '../../components/carrierRelationship/EProForm';
import EProSummary from '../../components/carrierRelationship/EProSummary';

import { sanitize } from '../../Models/EProNumber';

export class CarrierRelationshipDetails extends Component {
  state = {
    loadingCarrierDetails: true,
    loadingEProNumbers: true,
    carrier: {},
    openEProForm: false,
    isSubmitting: false,
    eProNumbers: [],
    errorSubmitting: null,
    locations: [],
    isDeleting: false
  };

  async componentDidMount() {
    await this._getLocations();
    await this._getData();
    await this._getEProNumbers();
  }

  _getLocations = async () => {
    const { client: ApolloClient } = this.props;

    try {
      const {
        orgInfo: { orgId }
      } = this.context.globalProps;

      const {
        data: {
          location: { all: locations }
        }
      } = await ApolloClient.query({
        query: locationsByOrgIdQuery,
        variables: { orgId }
      });

      this.setState({ locations });
    } catch (error) {
      this.setState({
        error
      });
    }
  };

  _getData = async () => {
    const {
      match: {
        params: { id }
      },
      client: ApolloClient
    } = this.props;

    try {
      this.setState({ loadingCarrierDetails: true });

      const {
        data: {
          carrier: { byId: carrier }
        }
      } = await ApolloClient.query({
        query: Queries.byId,
        variables: { id }
      });

      this.setState({
        carrier
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loadingCarrierDetails: false
      });
    }
  };

  _getEProNumbers = async () => {
    const { client: ApolloClient } = this.props;

    const {
      orgInfo: { orgId }
    } = this.context.globalProps;

    try {
      this.setState({ loadingEProNumbers: true });

      const {
        data: {
          ePro: { byCarrierId: eProNumbers }
        }
      } = await ApolloClient.query({
        query: getEProByCarrierIdQuery,
        variables: { carrierId: this.state.carrier.id, orgId },
        fetchPolicy: 'network-only'
      });

      this.setState({
        eProNumbers
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loadingEProNumbers: false
      });
    }
  };

  _handleActiveToggle = async isActive => {
    const { client: ApolloClient } = this.props;

    const { carrier } = this.state;

    try {
      const {
        data: {
          carrier: {
            update: { rowVersion }
          }
        }
      } = await ApolloClient.mutate({
        mutation: Mutations.update,
        variables: {
          carrier: {
            id: carrier.id,
            rowVersion: carrier.rowVersion,
            expired: !isActive
          }
        }
      });
      this.setState({
        carrier: {
          ...carrier,
          rowVersion
        }
      });
    } catch (error) {
      this.setState({
        error
      });
    }
  };

  _handleOpenNewEProForm = () => this.setState({ openEProForm: true });

  _handleCloseNewEProForm = () => this.setState({ openEProForm: false });

  _handleCreateEProNumbers = async values => {
    const { client: ApolloClient } = this.props;

    const { carrier } = this.state;

    this.setState({ isSubmitting: true });

    try {
      await ApolloClient.mutate({
        mutation: createEPro,
        variables: {
          ePro: {
            carrierId: carrier.id,
            ...sanitize(values)
          }
        }
      });
      this.setState({ isSubmitting: false, openEProForm: false });
      await this._getEProNumbers();
    } catch (errorSubmitting) {
      this.setState({
        errorSubmitting,
        isSubmitting: false
      });
    }
  };

  _handleActivateToggleEProNumber = async value => {
    const { client: ApolloClient } = this.props;

    try {
      await ApolloClient.mutate({
        mutation: updateEPro,
        variables: {
          ePro: {
            eProId: value.id,
            suspended: !value.suspended
          }
        }
      });
      await this._getEProNumbers();
    } catch (errorSubmitting) {
      this.setState({
        errorSubmitting
      });
    }
  };

  _handleDeleteEProNumber = async value => {
    const { client: ApolloClient } = this.props;
    this.setState({ isDeleting: true });

    try {
      await ApolloClient.mutate({
        mutation: deleteEPro,
        variables: {
          eProId: value.id
        }
      });
      this.setState({ isDeleting: false });
      await this._getEProNumbers();
    } catch (errorSubmitting) {
      this.setState({
        errorSubmitting
      });
    }
  };

  render() {
    const { t, classes } = this.props;
    const { carrier } = this.state;

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    return (
      <div>
        <div className={classes.titleWrapper}>
          {!this.state.loadingCarrierDetails && (
            <Fragment>
              <h1
                className={classnames(
                  classes.headline4,
                  classes.noMarginBottom,
                  classes.title
                )}
              >
                {carrier.name}
              </h1>
              <ToggleButton
                onChange={this._handleActiveToggle}
                value={carrier.active}
              />
            </Fragment>
          )}
        </div>
        <Grid container spacing={16}>
          <GridItem lg={6}>
            <FormItemContainer
              title={t('Carrier Details')}
              secondaryAction={
                <Button
                  size={'small'}
                  color={'primary'}
                  className={classnames(
                    classes.smallButton,
                    classes.actionButtons
                  )}
                  classes={{
                    label: classes.buttonLabel
                  }}
                  component={Link}
                  to={`/carrier-relationships/${carrier.id}/edit`}
                >
                  {t('Edit')}
                </Button>
              }
            >
              {this.state.loadingCarrierDetails ? (
                <LoadingIndicator />
              ) : (
                <Fragment>
                  <CarrierRelationshipBasicInfo
                    t={t}
                    classes={classes}
                    carrier={carrier}
                    isLoading={this.state.loadingCarrierDetails}
                  />
                  <CarrierRelationshipInsuranceInfo
                    t={t}
                    classes={classes}
                    carrier={carrier}
                    isLoading={this.state.loadingCarrierDetails}
                  />
                </Fragment>
              )}
            </FormItemContainer>
          </GridItem>
          <GridItem lg={6}>
            <FormItemContainer
              title={t('E-PRO Numbers')}
              secondaryAction={
                <IconButton
                  color={'primary'}
                  onClick={this._handleOpenNewEProForm}
                >
                  <AddIcon />
                </IconButton>
              }
            >
              {this.state.loadingEProNumbers ? (
                <LoadingIndicator />
              ) : (
                this.state.eProNumbers.map(eProNumber => (
                  <EProSummary
                    key={eProNumber.id}
                    eProNumber={eProNumber}
                    onActivateToggle={this._handleActivateToggleEProNumber}
                    onDelete={this._handleDeleteEProNumber}
                    locations={this.state.locations}
                    isDeleting={this.state.isDeleting}
                  />
                ))
              )}
            </FormItemContainer>
          </GridItem>
        </Grid>
        {this.state.openEProForm && (
          <CarrierRelationshipEProFormModal
            onClose={this._handleCloseNewEProForm}
            type={'PERSIST'}
            onSave={this._handleCreateEProNumbers}
            isSubmitting={this.state.isSubmitting}
            locations={this.state.locations}
          />
        )}
        {this.state.errorSubmitting && (
          <ErrorNotification message={this.state.errorSubmitting.message} />
        )}
      </div>
    );
  }
}

CarrierRelationshipDetails.contextType = AppContext;

const CarrierRelationshipDetailsWithTranslation = withNamespaces()(
  CarrierRelationshipDetails
);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    CarrierRelationshipDetailsStyles
  )
)(withApollo(CarrierRelationshipDetailsWithTranslation));
